/**
 * Orchestration Zod schemas v2: Actions, Runners, Runs, Jobs, Polling.
 */
import { z } from 'zod';

// ══════════════════════════════════════════
// REUSABLE PATTERNS
// ══════════════════════════════════════════

const slugRegex = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

const SlugSchema = z
  .string()
  .min(2, 'Slug deve ter pelo menos 2 caracteres')
  .max(64, 'Slug deve ter no máximo 64 caracteres')
  .regex(slugRegex, 'Slug deve ser lowercase com hífens (ex: analise-backlinks)');

const ExecutionModeSchema = z.enum(['runner', 'llm']);

const RunnerModeSchema = z.enum(['vps', 'openclaw']);

const IntegrationIdSchema = z
  .string()
  .min(1)
  .max(50)
  .regex(/^[a-z0-9_]+$/, 'Integration ID deve ser lowercase alfanumérico');

/** Structured integration requirement with optional key_name and label */
const IntegrationRefSchema = z.union([
  IntegrationIdSchema,
  z.object({
    provider: IntegrationIdSchema,
    key_name: z.string().min(1).max(100).optional(),
    label: z.string().max(200).optional(),
  }),
]);

/** Job type: generic or namespaced (e.g. crawl.fetch_pages) */
const JobTypeSchema = z
  .string()
  .min(2)
  .max(64)
  .regex(/^[a-z][a-z0-9_]*(\.[a-z][a-z0-9_]*)?$/, 'Job type deve ser "namespace.action" ou tipo simples');

// Known job types (for documentation/validation hints)
// http_fetch, scrape_html, seo_fetch_serp, generate_artifact_markdown,
// llm_prompt, ai_prompt, crawl.fetch_pages, serp.get_competitors,
// trello.create_cards, github.create_issue

/** Runner capability flags */
const CapabilityFlagSchema = z.enum([
  'http', 'browser', 'dns', 'github', 'trello',
  'cloudflare', 'email', 'ai', 'filesystem',
]);

/** Structured capabilities object */
const CapabilitiesSchema = z.object({
  flags: z.array(CapabilityFlagSchema).optional(),
  tools: z.array(z.string().max(50)).max(50).optional(),
  models: z.array(z.string().max(80)).max(10).optional(),
  os: z.string().max(50).optional(),
  version: z.string().max(20).optional(),
}).passthrough().default({});

/** Job step definition inside job_graph */
const JobStepSchema = z.object({
  key: z.string().min(1).max(64).regex(/^[a-z0-9_-]+$/, 'Step key deve ser lowercase alfanumérico com _ ou -'),
  name: z.string().min(1).max(120),
  type: JobTypeSchema,
  config: z.record(z.unknown()).default({}),
  depends_on: z.array(z.string().max(64)).max(20).default([]),
  max_attempts: z.number().int().min(1).max(10).optional(),
  timeout_sec: z.number().int().min(10).max(3600).optional(),
});

/** Validate job_graph array (structure only — cycle detection is server-side) */
const JobGraphSchema = z.array(JobStepSchema).max(50).default([]).superRefine((graph, ctx) => {
  // Unique keys
  const keys = new Set<string>();
  for (let i = 0; i < graph.length; i++) {
    const step = graph[i]!;
    if (keys.has(step.key)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Step key duplicada: "${step.key}"`,
        path: [i, 'key'],
      });
    }
    keys.add(step.key);
  }
  // depends_on references valid keys
  for (let i = 0; i < graph.length; i++) {
    const step = graph[i]!;
    for (const dep of step.depends_on ?? []) {
      if (!keys.has(dep)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `depends_on referencia key inexistente: "${dep}"`,
          path: [i, 'depends_on'],
        });
      }
      if (dep === step.key) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Step "${step.key}" não pode depender de si mesmo`,
          path: [i, 'depends_on'],
        });
      }
    }
  }
});

// ══════════════════════════════════════════
// 1. CreateActionSchema (Admin)
// ══════════════════════════════════════════

export const CreateActionSchema = z.object({
  agent_id: z.string().uuid('agent_id deve ser UUID'),
  slug: SlugSchema,
  name: z.string().min(2, 'Nome deve ter pelo menos 2 caracteres').max(120),
  description: z.string().max(2000).optional().nullable(),
  icon: z.string().max(10).optional().nullable(),
  execution_mode: ExecutionModeSchema.default('runner'),
  input_schema: z.record(z.unknown()).default({}),
  required_integrations: z.array(IntegrationRefSchema).max(20).default([]),
  job_graph: JobGraphSchema,
  estimated_duration_sec: z.number().int().positive().max(86400).optional().nullable(),
  is_active: z.boolean().default(true),
  llm_config: z.object({
    system_prompt: z.string().max(50000).optional(),
    model: z.string().max(100).optional(),
    max_tokens: z.number().int().min(100).max(8192).optional(),
    output_formats: z.array(z.enum(['markdown', 'docx', 'html', 'pdf', 'json', 'csv', 'txt'])).optional(),
  }).optional(),
}).superRefine((data, ctx) => {
  // LLM mode: job_graph is optional, but system_prompt is required
  if (data.execution_mode === 'llm' && (!data.job_graph || data.job_graph.length === 0)) {
    if (!data.llm_config?.system_prompt || data.llm_config.system_prompt.trim().length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Modo LLM requer system_prompt em llm_config',
        path: ['llm_config', 'system_prompt'],
      });
    }
    return; // Skip job_graph validation for LLM mode
  }

  // Runner mode: job_graph is required
  if (data.execution_mode === 'runner' && (!data.job_graph || data.job_graph.length === 0)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Modo Runner requer pelo menos 1 step no job_graph',
      path: ['job_graph'],
    });
  }

  // Validate that job_graph {{params.*}} placeholders match input_schema properties
  if (data.job_graph && data.job_graph.length > 0 && data.input_schema) {
    const schemaProps = new Set(Object.keys(data.input_schema?.properties ?? data.input_schema ?? {}));
    const placeholderRegex = /\{\{params\.([a-zA-Z0-9_.]+)\}\}/g;

    for (let i = 0; i < data.job_graph.length; i++) {
      const step = data.job_graph[i]!;
      const configStr = JSON.stringify(step.config ?? {});
      let match;
      while ((match = placeholderRegex.exec(configStr)) !== null) {
        const paramKey = match[1]!.split('.')[0]!;
        if (schemaProps.size > 0 && !schemaProps.has(paramKey)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Step "${step.key}": placeholder {{params.${match[1]}}} referencia "${paramKey}" que não existe no input_schema. Campos disponíveis: ${[...schemaProps].join(', ') || '(nenhum)'}`,
            path: ['job_graph', i, 'config'],
          });
        }
      }
    }
  }
});

export type CreateActionInput = z.infer<typeof CreateActionSchema>;

// ══════════════════════════════════════════
// 2. UpdateActionSchema (Admin)
// ══════════════════════════════════════════

export const UpdateActionSchema = z.object({
  name: z.string().min(2).max(120).optional(),
  description: z.string().max(2000).optional().nullable(),
  icon: z.string().max(10).optional().nullable(),
  execution_mode: ExecutionModeSchema.optional(),
  input_schema: z.record(z.unknown()).optional(),
  required_integrations: z.array(IntegrationRefSchema).max(20).optional(),
  job_graph: JobGraphSchema.optional(),
  estimated_duration_sec: z.number().int().positive().max(86400).optional().nullable(),
  is_active: z.boolean().optional(),
  llm_config: z.object({
    system_prompt: z.string().max(50000).optional(),
    model: z.string().max(100).optional(),
    max_tokens: z.number().int().min(100).max(8192).optional(),
    output_formats: z.array(z.enum(['markdown', 'docx', 'html', 'pdf', 'json', 'csv', 'txt'])).optional(),
  }).optional(),
});

export type UpdateActionInput = z.infer<typeof UpdateActionSchema>;

// ══════════════════════════════════════════
// 3. StartRunSchema (Customer)
// ══════════════════════════════════════════

export const StartRunSchema = z.object({
  action_id: z.string().uuid('action_id deve ser UUID'),
  project_id: z.string().uuid('project_id deve ser UUID').optional().nullable(),
  params: z.record(z.unknown()).default({}),
  runner_id: z.string().min(1, 'runner_id não pode ser vazio').optional().nullable(),
  /** Client-generated idempotency key to prevent duplicate runs on retry */
  idempotency_key: z.string().min(8).max(128).optional().nullable(),
  policy: z.object({
    max_attempts: z.number().int().min(1).max(10).default(3),
    timeout_sec: z.number().int().min(30).max(3600).default(300),
  }).default({}),
});

export type StartRunInput = z.infer<typeof StartRunSchema>;

// ══════════════════════════════════════════
// 4. RegisterRunnerSchema (Customer)
// ══════════════════════════════════════════

export const RegisterRunnerSchema = z.object({
  name: z
    .string()
    .min(2, 'Nome deve ter pelo menos 2 caracteres')
    .max(80, 'Nome deve ter no máximo 80 caracteres')
    .trim(),
  /** Runner mode: 'vps' (Node.js standalone) or 'openclaw' (plugin connector) */
  runner_mode: RunnerModeSchema.default('vps'),
  capabilities: CapabilitiesSchema,
});

export type RegisterRunnerInput = z.infer<typeof RegisterRunnerSchema>;

// ══════════════════════════════════════════
// 5. UpdateRunnerSchema (Customer)
// ══════════════════════════════════════════

export const UpdateRunnerSchema = z.object({
  name: z.string().min(2).max(80).trim().optional(),
  capabilities: CapabilitiesSchema.optional(),
  status: z.enum(['online', 'offline', 'disabled']).optional(),
});

export type UpdateRunnerInput = z.infer<typeof UpdateRunnerSchema>;

// ══════════════════════════════════════════
// 6. RunnerPullQuerySchema (Runner — claim jobs)
// ══════════════════════════════════════════

export const RunnerPullQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(5).default(1),
  lease_seconds: z.coerce.number().int().min(30).max(600).default(120),
  job_type: z.string().max(64).optional(),
});

export type RunnerPullQuery = z.infer<typeof RunnerPullQuerySchema>;

// ══════════════════════════════════════════
// 7. RunnerReportSchema (Runner — report job result)
// ══════════════════════════════════════════

export const RunnerReportSchema = z.object({
  job_id: z.string().uuid('job_id deve ser UUID'),
  status: z.enum(['done', 'failed']),
  output: z.any().optional().nullable(),
  logs: z.string().max(20_000, 'Logs limitado a 20.000 caracteres').optional().nullable(),
  error: z.string().max(5000).optional().nullable(),
  duration_ms: z.number().int().nonnegative().optional().nullable(),
  artifacts: z.array(z.object({
    name: z.string().min(1).max(200),
    type: z.enum(['report_json', 'report_md', 'report_html', 'file']).default('report_json'),
    mime: z.string().max(100).default('application/json'),
    content: z.string().max(5_000_000),
  })).max(10).optional().default([]),
});

export type RunnerReportInput = z.infer<typeof RunnerReportSchema>;

// ══════════════════════════════════════════
// 8. RunnerHeartbeatSchema (Runner)
// ══════════════════════════════════════════

export const RunnerHeartbeatSchema = z.object({
  capabilities: CapabilitiesSchema.optional(),
  load: z.number().min(0).max(100).optional(),
  active_jobs: z.number().int().nonnegative().max(50).optional(),
  workspace_size_bytes: z.number().nonnegative().optional(),
  server_ip: z.string().optional(),
  server_region: z.string().optional(),
});

export type RunnerHeartbeatInput = z.infer<typeof RunnerHeartbeatSchema>;

// ══════════════════════════════════════════
// 9. RunnerProgressSchema (Runner — partial progress)
// ══════════════════════════════════════════

export const RunnerProgressSchema = z.object({
  job_id: z.string().uuid('job_id deve ser UUID'),
  message: z.string().max(1000).optional(),
  progress: z.number().min(0).max(100).optional(),
});

export type RunnerProgressInput = z.infer<typeof RunnerProgressSchema>;

// ══════════════════════════════════════════
// 10. ListActionsQuerySchema (Customer)
// ══════════════════════════════════════════

export const ListActionsQuerySchema = z.object({
  agent_id: z.string().uuid().optional(),
  category_slug: z.string().max(64).optional(),
  project_id: z.string().uuid().optional(),
  active_only: z.coerce.boolean().default(true),
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(20),
});

export type ListActionsQuery = z.infer<typeof ListActionsQuerySchema>;

// ══════════════════════════════════════════
// 11. ListRunsQuerySchema (Customer)
// ══════════════════════════════════════════

export const ListRunsQuerySchema = z.object({
  project_id: z.string().uuid().optional(),
  action_id: z.string().uuid().optional(),
  status: z.enum(['queued', 'running', 'done', 'failed', 'cancelled']).optional(),
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(20),
});

export type ListRunsQuery = z.infer<typeof ListRunsQuerySchema>;

// ══════════════════════════════════════════
// 12. ArtifactDownloadQuerySchema (Customer)
// ══════════════════════════════════════════

export const ArtifactDownloadQuerySchema = z.object({
  download: z.coerce.boolean().default(false),
});

export type ArtifactDownloadQuery = z.infer<typeof ArtifactDownloadQuerySchema>;
